import { ExclamationTriangleIcon, NoSymbolIcon, ShieldExclamationIcon } from '@heroicons/react/20/solid'
import type { CpqViolation } from '../../lib/api'

interface Props {
  violations: CpqViolation[]
  compact?: boolean
}

export function CpqViolationIndicator({ violations, compact = false }: Props) {
  if (!violations || violations.length === 0) return null

  // Get the most severe violation
  const blockedCount = violations.filter(v => v.action === 'block').length
  const approvalCount = violations.filter(v => v.action === 'require_approval').length
  const warnCount = violations.filter(v => v.action === 'warn').length

  const mostSevere = blockedCount > 0 ? 'block' : approvalCount > 0 ? 'require_approval' : 'warn'

  const getIcon = () => {
    switch (mostSevere) {
      case 'block':
        return <NoSymbolIcon className="h-4 w-4 text-red-500" />
      case 'require_approval':
        return <ShieldExclamationIcon className="h-4 w-4 text-orange-500" />
      case 'warn':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
    }
  }

  const getBadgeClasses = () => {
    switch (mostSevere) {
      case 'block':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'require_approval':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'warn':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
  }

  if (compact) {
    return (
      <div className="group relative">
        {getIcon()}
        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64">
          <div className={`rounded-lg border p-3 shadow-lg ${getBadgeClasses()}`}>
            <div className="text-xs font-medium mb-2">
              {blockedCount > 0 && `${blockedCount} blocked`}
              {approvalCount > 0 && `${blockedCount > 0 ? ', ' : ''}${approvalCount} require approval`}
              {warnCount > 0 && `${blockedCount > 0 || approvalCount > 0 ? ', ' : ''}${warnCount} warning${warnCount > 1 ? 's' : ''}`}
            </div>
            <ul className="space-y-1">
              {violations.slice(0, 3).map((v, i) => (
                <li key={i} className="text-xs">
                  {v.message}
                </li>
              ))}
              {violations.length > 3 && (
                <li className="text-xs text-gray-600">
                  +{violations.length - 3} more...
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`rounded-lg border p-3 ${getBadgeClasses()}`}>
      <div className="flex items-start gap-2">
        {getIcon()}
        <div className="flex-1">
          <div className="text-sm font-medium mb-1">
            CPQ Violations ({violations.length})
          </div>
          <ul className="space-y-1">
            {violations.map((v, i) => (
              <li key={i} className="text-xs flex items-start gap-1">
                <span className="inline-block mt-1">
                  {v.action === 'block' && <NoSymbolIcon className="h-3 w-3" />}
                  {v.action === 'require_approval' && <ShieldExclamationIcon className="h-3 w-3" />}
                  {v.action === 'warn' && <ExclamationTriangleIcon className="h-3 w-3" />}
                </span>
                <span>{v.message}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export function CpqViolationBadge({ action }: { action: 'warn' | 'require_approval' | 'block' }) {
  const getIcon = () => {
    switch (action) {
      case 'block':
        return <NoSymbolIcon className="h-3 w-3" />
      case 'require_approval':
        return <ShieldExclamationIcon className="h-3 w-3" />
      case 'warn':
        return <ExclamationTriangleIcon className="h-3 w-3" />
    }
  }

  const getClasses = () => {
    switch (action) {
      case 'block':
        return 'bg-red-100 text-red-700'
      case 'require_approval':
        return 'bg-orange-100 text-orange-700'
      case 'warn':
        return 'bg-yellow-100 text-yellow-700'
    }
  }

  const getLabel = () => {
    switch (action) {
      case 'block':
        return 'Blocked'
      case 'require_approval':
        return 'Approval Required'
      case 'warn':
        return 'Warning'
    }
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getClasses()}`}>
      {getIcon()}
      {getLabel()}
    </span>
  )
}
